import {
  CircularProgress,
  DraggableVirtualList,
  IDraggableItem,
  InlineTeaserView,
  NocfoIcon,
  Progress,
  TextTooltip
} from '@components'
import { useBusinessContext } from '@context'
import { useQueryParam } from '@hooks'
import { IDocument, useDocumentLimits } from '@query'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import React, { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { DocumentListItem } from './DocumentListItem'
import { DocumentTrash } from './DocumentTrash'
import { useTranslation } from 'react-i18next'

interface Props {
  documents: IDocument[]
  onScrollBottom: () => Promise<any>
}

interface IDateBadge {
  id: string
  title: string
  startOfMonth: Date
}

interface IDocumentElement {
  id: number
  isFirstOfType: boolean
  isLastOfType: boolean
  document: IDocument
}

interface DraggableBadgeOrDocument {
  type: 'document' | 'badge'
  allowDrag: boolean
  allowDrop: boolean
  item: IDateBadge | IDocumentElement
}

export const DocumentList: React.FC<Props> = ({ documents, onScrollBottom }) => {
  const [selectedId] = useQueryParam<number>('id', value => parseInt(value, 10))

  const documentsAndBadges = useMemo<DraggableBadgeOrDocument[]>(() => {
    const docsAndBadges: DraggableBadgeOrDocument[] = []
    const seenDocuments = new Set<number>()

    documents.forEach((document, index) => {
      const lastDoc = documents[index - 1]
      const nextDoc = documents[index + 1]

      const thisDate = dayjs(document.date, 'YYYY-MM-DD').format('MMMM YYYY')
      const lastDate = lastDoc && dayjs(lastDoc.date, 'YYYY-MM-DD').format('MMMM YYYY')
      const nextDate = nextDoc && dayjs(nextDoc.date, 'YYYY-MM-DD').format('MMMM YYYY')

      const isFirstOfType = thisDate !== lastDate
      const isLastOfType = thisDate !== nextDate

      // Duplicate check in case TanStack Query has re-fetched only one page
      // which can briefly cause duplicates before the next page is fetched
      if (seenDocuments.has(document.id)) return
      seenDocuments.add(document.id)

      if (isFirstOfType) {
        docsAndBadges.push({
          type: 'badge',
          allowDrag: false,
          allowDrop: false,
          item: {
            id: thisDate,
            title: thisDate,
            startOfMonth: dayjs(document.date).startOf('month').toDate()
          }
        })
      }

      docsAndBadges.push({
        type: 'document',
        allowDrag: true,
        allowDrop: false,
        item: {
          id: document.id,
          isFirstOfType,
          isLastOfType,
          document
        }
      })
    })

    return docsAndBadges
  }, [documents])

  return (
    <DocumentListWrapper>
      <DraggableVirtualList
        items={documentsAndBadges}
        itemType="document"
        itemSize={90}
        onScrollBottom={onScrollBottom}
        render={(item, { isSelected }) => {
          if (item.type === 'badge') {
            const dateBadge = item.item as IDateBadge

            return (
              <DateBadgeWrapper key={dateBadge.id}>
                <DateBadge startOfMonth={dateBadge.startOfMonth}>{dateBadge.title}</DateBadge>
              </DateBadgeWrapper>
            )
          } else {
            const { document, isLastOfType, isFirstOfType } = item.item as IDocumentElement

            return (
              <DocumentListItem
                key={`document-${document.id}`}
                document={document}
                isSelected={isSelected || document.id === selectedId}
                isFirstOfType={isFirstOfType}
                isLastOfType={isLastOfType}
              />
            )
          }
        }}
        renderDrag={items => <DragLayer items={items} />}
      />

      <DocumentTrash />
    </DocumentListWrapper>
  )
}

const DragLayer: React.FC<{ items: IDraggableItem[] }> = ({ items }) => {
  const totalItems = items.length
  const renderableItems = items.slice(0, 5)
  return (
    <StyledDragLayer>
      {renderableItems.map(({ item }, index) => (
        <StyledDragItem key={`document-${item.id}`} $index={index} $count={renderableItems.length}>
          <DocumentListItem
            document={(item as IDocumentElement).document}
            isSelected={false}
            isFirstOfType={true}
            isLastOfType={true}
          />
        </StyledDragItem>
      ))}
      <DragCount>{totalItems}</DragCount>
    </StyledDragLayer>
  )
}

const DateBadge: React.FC<{ children: React.ReactNode; startOfMonth: Date }> = ({
  children,
  startOfMonth
}) => {
  const { data: business } = useBusinessContext()
  const { t } = useTranslation()
  const theme = useTheme()
  const { data: limits, isLoading } = useDocumentLimits(startOfMonth)
  const capReached = limits?.usage_percentage >= 100

  const tooltip = capReached
    ? t('documentList.documentLimit.tooltip_limitReached', {
        documentLimit: limits?.limit ?? 0
      })
    : t('documentList.documentLimit.tooltip_progress', {
        usedDocuments: limits?.used ?? 0,
        documentLimit: limits?.limit ?? 0
      })

  const getTooltipProgressColor = (progress: number) => {
    if (progress < 70) return theme.colors.success
    if (progress < 90) return theme.colors.warning
    return theme.colors.error
  }

  // Hide tooltip and progress for agency subscribed businesses as they have unlimited documents
  if (business?.subscription_source === 'AGENCY') {
    return (
      <DateRowContainer>
        <StyledDateBadge style={{ color: capReached && theme.colors.warning }}>
          {children}
        </StyledDateBadge>
      </DateRowContainer>
    )
  }

  return (
    <TextTooltip
      maxWidth={340}
      placement="bottom"
      zIndex={49}
      tooltip={
        <div>
          <div>{tooltip}</div>
          <br />
          <Progress
            initialAnimation={true}
            percent={limits?.usage_percentage ?? 0}
            progressColor={getTooltipProgressColor(limits?.usage_percentage ?? 0)}
          />
          {capReached && (
            <>
              <br />
              <InlineTeaserView
                icon={<NocfoIcon />}
                iconText={t('documentList.documentLimit.upgradeHeader')}
                text={t('documentList.documentLimit.upgradeMessage')}
              />
            </>
          )}
        </div>
      }
    >
      <DateRowContainer>
        <StyledDateBadge>{children}</StyledDateBadge>

        <CircularProgress
          size={18}
          progress={limits?.usage_percentage ?? 0}
          trackColor={`${theme.colors.metalGray}44`}
          progressColor={capReached ? theme.colors.error : `${theme.colors.metalGray}cc`}
          strokeWidth={2}
          isLoading={isLoading}
        />
      </DateRowContainer>
    </TextTooltip>
  )
}

const StyledDragLayer = styled.div`
  position: relative;
  max-width: 400px;
`

const StyledDragItem = styled(motion.div)<{ $index: number; $count: number }>`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  width: 100%;
  overflow: hidden;
  box-shadow: 0px 5px 10px 1px rgba(0, 0, 0, 0.1);
  z-index: ${({ $count, $index }) => 10 + $count - $index};
  transform: translate(${({ $index }) => $index * 4}px, ${({ $index }) => $index * 4}px);
`

const DragCount = styled.div`
  background: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-weight: bold;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  border-radius: 10rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  right: 0;
  min-width: 1.6rem;
  height: 1.6rem;
  transform: translate(50%, 50%);
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  z-index: 20;
`

const DocumentListWrapper = styled.div`
  flex: 1;
  height: 100%;
  overflow: auto;
  position: relative;
  overflow: hidden;
`

const DateBadgeWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;

  & > * {
    align-self: center;
  }
`

const DateRowContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.4rem;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledDateBadge = styled.div`
  text-transform: uppercase;
  white-space: nowrap;
  background: ${({ theme }) => theme.colors.neutralBlack};
  color: ${({ theme }) => theme.colors.metalGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.neutralGray};
`
